import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";
import { Sentry } from "@/lib/sentry";


interface UserPermissions {
  page_dashboard: boolean;
  page_documents: boolean;
  page_progress: boolean;
  page_questions: boolean;
}

const defaultPermissions: UserPermissions = {
  page_dashboard: true,
  page_documents: true,
  page_progress: true,
  page_questions: true,
};

interface EmployeePermissions {
  employee_role: string;
  page_dashboard: boolean;
  page_announcements: boolean;
  page_crew_roster: boolean;
  page_safety: boolean;
  page_directory: boolean;
  page_documents: boolean;
  page_questions: boolean;
  page_progress: boolean;
  page_fleet: boolean;
  page_onboarding: boolean;
  page_training: boolean;
  page_contracts: boolean;
  page_compliance: boolean;
  page_emergency_contacts: boolean;
  page_vendors: boolean;
  page_supply_chain: boolean;
  page_commercial: boolean;
  page_security_docs: boolean;
  military_access: boolean;
}

const defaultEmployeePermissions: EmployeePermissions = {
  employee_role: "crew",
  page_dashboard: true,
  page_announcements: true,
  page_crew_roster: true,
  page_safety: true,
  page_directory: true,
  page_documents: true,
  page_questions: true,
  page_progress: true,
  page_fleet: true,
  page_onboarding: true,
  page_training: true,
  page_contracts: true,
  page_compliance: true,
  page_emergency_contacts: true,
  page_vendors: true,
  page_supply_chain: true,
  page_commercial: true,
  page_security_docs: false,
  military_access: false,
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  companyId: string | null;
  companyName: string | null;
  fullName: string | null;
  isAdmin: boolean;
  isEmployee: boolean;
  isAuditor: boolean;
  isApproved: boolean;
  permissions: UserPermissions;
  employeePermissions: EmployeePermissions;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [fullName, setFullName] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const [isAuditor, setIsAuditor] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [permissions, setPermissions] = useState<UserPermissions>(defaultPermissions);
  const [employeePermissions, setEmployeePermissions] = useState<EmployeePermissions>(defaultEmployeePermissions);

  const fetchProfile = async (userId: string) => {
    const { data: profile } = await supabase
      .from("profiles")
      .select("company_id, full_name, is_approved")
      .eq("user_id", userId)
      .single();

    if (profile) {
      setCompanyId(profile.company_id);
      setFullName(profile.full_name);
      setIsApproved((profile as any).is_approved ?? false);

      const { data: company } = await supabase
        .from("companies")
        .select("name")
        .eq("id", profile.company_id)
        .single();

      if (company) setCompanyName(company.name);
    }

    // Check roles server-side via DB function
    const [{ data: adminCheck }, { data: employeeCheck }, { data: auditorCheck }] = await Promise.all([
      supabase.rpc("has_role", { _user_id: userId, _role: "admin" }),
      supabase.rpc("has_role", { _user_id: userId, _role: "employee" }),
      supabase.rpc("has_role", { _user_id: userId, _role: "auditor" as any }),
    ]);
    setIsAdmin(!!adminCheck);
    setIsEmployee(!!employeeCheck);
    setIsAuditor(!!auditorCheck);

    Sentry.setUser({
      id: userId,
      email: (await supabase.auth.getUser()).data.user?.email ?? undefined,
      role: adminCheck ? "admin" : employeeCheck ? "employee" : auditorCheck ? "auditor" : "client",
    });


    // Fetch page permissions
    const { data: perms } = await supabase
      .from("user_permissions")
      .select("page_dashboard, page_documents, page_progress, page_questions")
      .eq("user_id", userId)
      .maybeSingle();
    if (perms) {
      setPermissions(perms as UserPermissions);
    } else {
      setPermissions(defaultPermissions);
    }

    // Fetch employee permissions
    const { data: empPerms } = await supabase
      .from("employee_permissions")
      .select("employee_role, page_dashboard, page_announcements, page_crew_roster, page_safety, page_directory, page_documents, page_questions, page_progress, page_supply_chain, page_commercial, page_security_docs, military_access")
      .eq("user_id", userId)
      .maybeSingle();
    if (empPerms) {
      setEmployeePermissions({ ...defaultEmployeePermissions, ...empPerms } as EmployeePermissions);
    } else {
      setEmployeePermissions(defaultEmployeePermissions);
    }
  };

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        if (session?.user) {
          // Mark profile as loading immediately so route guards wait for roles
          setProfileLoading(true);
        }
        if (!session?.user) {
          setCompanyId(null);
          setCompanyName(null);
          setFullName(null);
          setIsAdmin(false);
          setIsEmployee(false);
          setIsAuditor(false);
          setIsApproved(false);
          setPermissions(defaultPermissions);
          setEmployeePermissions(defaultEmployeePermissions);
          setLoading(false);
          Sentry.setUser(null);
        }

        // NOTE: Do NOT log "login" here. onAuthStateChange fires SIGNED_IN on
        // every token refresh / tab focus / session restore, which inflates
        // last-login timestamps. Real logins are recorded in signIn() below.
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (!session?.user) {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch profile whenever user changes
  useEffect(() => {
    if (user) {
      setProfileLoading(true);
      fetchProfile(user.id).finally(() => {
        setProfileLoading(false);
        setLoading(false);
      });
    }
  }, [user?.id]);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    // Record a real login event only on successful credential sign-in.
    if (!error && data?.user) {
      supabase.from("audit_logs").insert({
        user_id: data.user.id,
        action: "login",
        resource_type: "auth",
        resource_id: data.user.id,
        details: { email: data.user.email },
      }).then(({ error: e }) => {
        if (e) console.error("Audit log insert failed:", e.message);
      });
    }
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading: loading || profileLoading, signIn, signOut, companyId, companyName, fullName, isAdmin, isEmployee, isAuditor, isApproved, permissions, employeePermissions }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
